import { Dispatch, ReactElement, SetStateAction, useMemo } from "react";
import CollapsibleTable from "@/components/02-organisms/CollapsibleTable/CollapsibleTable.tsx";
import { Lupa, PagedLuvatByJarjestajatResponse } from "@/stores/luvat";
import PageRowIndicator from "@/components/01-molecules/PageRowIndicator";
import { CircularProgress, List, ListItem, Typography } from "@mui/material";
import { getKoulutustyyppiLabel } from "@/utils/koulutustyyppi.ts";
import education from "@/i18n/definitions/education";
import { useIntl } from "react-intl";
import common from "@/i18n/definitions/common";
import { getLocalizedName } from "@/utils/localization.ts";
import PageNavigator from "@/components/01-molecules/PageNavigator";
import Dropdown from "@/components/00-atoms/Dropdown";
import { SearchState, SortState } from "@/components/02-organisms/CollapsibleTable/types.ts";
import { Link } from "react-router-dom";
import { localizeRouteKey, getKoulutusmuodot } from "@/utils/common";
import { AppRoute } from "@/routes";
import { values, find } from "ramda";

interface Props {
  jarjestajat: PagedLuvatByJarjestajatResponse;
  pageNo: number;
  pageSize: number;
  setPageNo: Dispatch<SetStateAction<number>>;
  setPageSize: Dispatch<SetStateAction<number>>;
  selectedKoulutustyyppi: string | null;
  setSelectedKoulutustyyppi: Dispatch<SetStateAction<string | null>>;
  sortedBy: SortState | null;
  onSortChange: (sort: SortState) => void;
  searchBy: SearchState;
  onSearchChange: (search: SearchState) => void;
  openRows: Set<string>;
  onOpenRowsChange: (openRows: Set<string>) => void;
  isLoading?: boolean;
}

const Jarjestajaluettelo = ({
  jarjestajat,
  pageNo,
  pageSize,
  setPageNo,
  setPageSize,
  selectedKoulutustyyppi,
  setSelectedKoulutustyyppi,
  sortedBy,
  onSortChange,
  searchBy,
  onSearchChange,
  openRows,
  onOpenRowsChange,
  isLoading = false
}: Props): ReactElement => {
  const intl = useIntl();
  const lang = intl.locale.split(/[-_]/)[0].toLowerCase() === "sv" ? "sv" : "fi";

  /** Tallentaa taulukon tilan sessionStorageen ennen lupasivulle siirtymistä. */
  const saveTableState = () => {
    const state = JSON.stringify({
      pageNo,
      pageSize,
      sortedBy,
      searchBy,
      selectedKoulutustyyppi,
      openRows: Array.from(openRows)
    });
    sessionStorage.setItem("kaikkiJarjestajat.tableState", state);
  };

  /**
   * Builds the route URL for a single lupa.
   */
  const getLupaRoute = (lupa: Lupa): string | undefined => {
    const koulutusmuodot = values(getKoulutusmuodot(intl)) as Array<
      Record<string, string>
    >;
    const koulutusmuoto = find(
      (km: Record<string, string>) =>
        km.koulutustyyppi === (lupa.koulutustyyppi ?? ""),
      koulutusmuodot
    );
    if (!koulutusmuoto) return undefined;
    const id =
      lupa.koulutustyyppi === "1" ||
      lupa.koulutustyyppi === "2" ||
      lupa.koulutustyyppi === "3" ||
      lupa.koulutustyyppi === "5"
        ? lupa.uuid
        : lupa.jarjestajaOid;
    return localizeRouteKey(intl.locale, AppRoute.Jarjestamislupa, intl, {
      id,
      koulutusmuoto: koulutusmuoto.kebabCase
    });
  };

  /**
   * Sentinel-arvot MUI Select:lle, jolle null ei kelpaa controlled-arvoksi.
   * "": alkutila – näyttää placeholderin, ei ole optiolistassa
   * __none__: käyttäjä on valinnut "Ei valintaa" – näkyy kentässä tekstinä
   * __ammatillinen__: ammatillinen koulutus – lähetetään backendille tyhjänä ""
   * Molemmat "" ja __none__ tarkoittavat backendille "ei suodatusta".
   */
  const KOULUTUSTYYPPI_NONE = "__none__";
  const KOULUTUSTYYPPI_AMMATILLINEN = "__ammatillinen__";

  const koulutustyyppiOptions = [
    { value: KOULUTUSTYYPPI_NONE, label: intl.formatMessage(common.noSelection) },
    { value: "1", label: intl.formatMessage(education.preAndBasicEducation) },
    { value: "5", label: intl.formatMessage(education.tuva) },
    { value: "2", label: intl.formatMessage(education.highSchoolEducation) },
    { value: KOULUTUSTYYPPI_AMMATILLINEN, label: intl.formatMessage(education.vocationalEducation) },
    { value: "3", label: intl.formatMessage(education.vstEducation) },
    { value: "4", label: intl.formatMessage(education.taiteenPerusopetus) }
  ];

  const onKoulutustyyppiChange = (
    _: unknown,
    { selectedOption }: { selectedOption: string | null }
  ) => {
    setPageNo(1); // resetoidaan sivu dropdown-valinnan vaihtuessa
    setSelectedKoulutustyyppi(selectedOption);
  };

  const columns = useMemo(() => [
    {
      id: "jarjestaja",
      label: intl.formatMessage(education.koulutuksenJarjestaja),
      sortKey: `jarjestaja.nimi.${lang}`,
      searchKey: `jarjestaja.nimi.${lang}`
    },
    {
      id: "maakunta",
      label: intl.formatMessage(common.homeCounty),
      sortKey: `jarjestaja.maakuntaKoodi.metadata.nimi.${lang}`,
      searchKey: `jarjestaja.maakuntaKoodi.metadata.nimi.${lang}`
    }
  ], [lang, intl]);

  const rows = jarjestajat.content.map(jarjestaja => {
    const maakunnat = Array.from(
      new Set(
        jarjestaja.luvat
          .map(l =>
            getLocalizedName(l.jarjestaja?.maakuntaKoodi?.metadata, intl.locale)
          )
          .filter((m): m is string => Boolean(m))
      )
    );

    return {
      id: jarjestaja.jarjestajaOid,
      cells: [jarjestaja.nimi, maakunnat.join(", ")],
      collapseContent: (
        <List dense>
          {jarjestaja.luvat.map(lupa => {
            const route = getLupaRoute(lupa);
            const label = getKoulutustyyppiLabel(intl, lupa.koulutustyyppi);
            return (
              <ListItem key={lupa.uuid}>
                {route ? (
                  <Link
                    to={route}
                    className="underline text-blue-700 hover:text-blue-900"
                    title={label}
                    onClick={saveTableState}>
                    <Typography variant="body2">{label}</Typography>
                  </Link>
                ) : (
                  <Typography variant="body2">{label}</Typography>
                )}
              </ListItem>
            );
          })}
        </List>
      )
    };
  });

  return (
    <div className="mx-auto w-full mb-16">
      <PageRowIndicator
        totalCount={jarjestajat?.totalElements}
        pageNo={pageNo}
        pageSize={pageSize}
      />
      <div className="flex items-center justify-between my-2 lg:mt-0 lg:mr-2">
        <div className="w-2/6">
          <Dropdown
            emptyMessage={intl.formatMessage(common.filterByKoulutusmuoto)}
            label={intl.formatMessage(common.filterByKoulutusmuoto)}
            options={koulutustyyppiOptions}
            value={selectedKoulutustyyppi ?? ""}
            onChanges={onKoulutustyyppiChange}
            fullWidth
          />
        </div>
      </div>
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-x-0 top-0 flex justify-center z-10 pt-8 pointer-events-none">
            <CircularProgress size={32} aria-label="Ladataan" />
          </div>
        )}
      <CollapsibleTable
        columns={columns}
        rows={rows}
        sortedBy={sortedBy}
        onSortChange={sort => {
          setPageNo(1);
          onSortChange(sort);
        }}
        searchBy={searchBy}
        onSearchChange={search => {
          setPageNo(1);
          onSearchChange(search);
        }}
        openRows={openRows}
        onOpenRowsChange={onOpenRowsChange}
      />
      </div>
      <PageNavigator
        pageNo={pageNo}
        pageSize={pageSize}
        totalPages={jarjestajat.totalPages}
        setPageNo={setPageNo}
        setPageSize={setPageSize}
      />
    </div>
  );
};

export default Jarjestajaluettelo;